import React, { useState } from 'react';
import { useResearchDemo } from '../../contexts/ResearchDemoContext';
import { concepts, predicates, researchSources } from '../../data/researchDemo';
import type { ProposalDraft } from '../../types/researchDemo';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { formatId } from '../../utils/demoStorage';
import { errorClass, fieldClass, glassPanel, labelClass, linkButton, primaryButton } from '../../utils/demoUi';
import { passageFor, sourceById, validateProposal } from '../../utils/researchDemo';
export function ProposeRelationship() {
  const {
    state,
    dispatch,
    openClaim
  } = useResearchDemo();
  const draft = state.ui.proposalDraft;
  const [attempted, setAttempted] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const errors = attempted ? validateProposal(draft) : {};
  const source = sourceById(draft.sourceId);
  const passage = passageFor(draft.sourceId, draft.passageId);
  const update = (patch: Partial<ProposalDraft>) => {
    setSubmittedId(null);
    dispatch({
      type: 'setProposalDraft',
      draft: patch
    });
  };
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (Object.keys(validateProposal(draft)).length > 0) return;
    const id = formatId('REL', state.counters.claim + 1);
    dispatch({
      type: 'submitProposal'
    });
    setAttempted(false);
    setSubmittedId(id);
  };
  const errorFor = (key: keyof ProposalDraft) => errors[key] ? <p id={`${key}-error`} className={errorClass} data-id="element-2165" data-mp="23ays5jkt4k9i">
        {errors[key]}
      </p> : null;
  const describe = (key: keyof ProposalDraft) => errors[key] ? {
    'aria-invalid': true,
    'aria-describedby': `${key}-error`
  } : {};
  const conceptSelect = (key: 'subjectId' | 'objectId', label: string) => <div data-id="element-2166" data-mp="3tno8sjztbobf">
      <label htmlFor={key} className={labelClass} data-id="element-2167" data-mp="3tnejkymldl6s">
        {label}
      </label>
      <select id={key} value={draft[key]} onChange={event => update({
      [key]: event.target.value
    })} className={fieldClass} {...describe(key)} data-id="element-2168" data-mp="3oobfbgefrizv">
        <option value="" data-id="element-2169" data-mp="blprdsh5mfcn">Choose…</option>
        {concepts.map(concept => <option key={concept.id} value={concept.id} data-id="element-2170" data-mp="gf0x1qa8cq2z">
            {concept.label} ({concept.role})
          </option>)}
      </select>
      {errorFor(key)}
    </div>;
  return <div data-id="element-2171" data-mp="7kw491bjd4kt">
      <DemoSectionHeader title="Propose Relationship" description="A manual proposal grounded in one exact passage. Submitting creates a Proposed record for review — it is not published to approved knowledge." data-id="element-2172" data-mp="7mqki2bv48ik" />

      {submittedId && <div role="status" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-teal/30 bg-teal-pale px-5 py-4" data-id="element-2173" data-mp="ldc8u6k35anu">
          <p className="text-sm text-ink-dark" data-id="element-2174" data-mp="qalwbgnwzc91">
            <span className="font-semibold" data-id="element-2175" data-mp="363kryxhinmc3">{submittedId}</span> submitted for review with status Proposed.
          </p>
          <button type="button" onClick={() => openClaim(submittedId)} className={linkButton} data-id="element-2176" data-mp="3b2nyynh242mp">
            Open evidence record
          </button>
        </div>}

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]" data-id="element-2177" data-mp="2yjsbkikwejns">
        <form onSubmit={submit} noValidate className={`${glassPanel} p-6 lg:p-8`} data-id="element-2178" data-mp="2ym4q0lrg6bw3">
          <fieldset data-id="element-2179" data-mp="3chgxzuum5yuy">
            <legend className="font-heading text-base font-bold text-ink-dark" data-id="element-2180" data-mp="37kgqdfi48o1q">Relationship</legend>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-2181" data-mp="2juvwi1kfc6do">
              {conceptSelect('subjectId', 'Subject concept')}
              <div data-id="element-2182" data-mp="2fubzqz3rj4s2">
                <label htmlFor="predicate" className={labelClass} data-id="element-2183" data-mp="2fuvjmlcz438q">
                  Relationship predicate
                </label>
                <select id="predicate" value={draft.predicate} onChange={event => update({
                predicate: event.target.value as ProposalDraft['predicate']
              })} className={fieldClass} {...describe('predicate')} data-id="element-2184" data-mp="2tmjvqon82rpp">
                  <option value="" data-id="element-2185" data-mp="29saecv013s1p">Choose…</option>
                  {predicates.map(predicate => <option key={predicate.id} value={predicate.id} data-id="element-2186" data-mp="2eqvbgljzhvlx">
                      {predicate.label}
                    </option>)}
                </select>
                {errorFor('predicate')}
              </div>
              {conceptSelect('objectId', 'Object concept')}
            </div>
          </fieldset>

          <fieldset className="mt-8" data-id="element-2187" data-mp="2shqh7oz6rwla">
            <legend className="font-heading text-base font-bold text-ink-dark" data-id="element-2188" data-mp="2skw736fn32i2">Evidence</legend>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-2189" data-mp="2xjfsn5qbmc9w">
              <div data-id="element-2190" data-mp="32hh9rl46s1iq">
                <label htmlFor="sourceId" className={labelClass} data-id="element-2191" data-mp="32ev3rpwy1u2m">
                  Source document
                </label>
                <select id="sourceId" value={draft.sourceId} onChange={event => update({
                sourceId: event.target.value,
                passageId: ''
              })} className={fieldClass} {...describe('sourceId')} data-id="element-2192" data-mp="182yadrexye4x">
                  <option value="" data-id="element-2193" data-mp="3llkr10aeaofl">Choose…</option>
                  {researchSources.map(item => <option key={item.id} value={item.id} data-id="element-2194" data-mp="3hj7mgf7dwwke">
                      {item.id} — {item.title}
                    </option>)}
                </select>
                {errorFor('sourceId')}
              </div>
              <div data-id="element-2195" data-mp="d47r4lz42lsj">
                <label htmlFor="passageId" className={labelClass} data-id="element-2196" data-mp="d3evag1oxgp6">
                  Supporting passage
                </label>
                <select id="passageId" value={draft.passageId} onChange={event => update({
                passageId: event.target.value
              })} disabled={!source} className={fieldClass} {...describe('passageId')} data-id="element-2197" data-mp="44kqw3i4bm12">
                  <option value="" data-id="element-2198" data-mp="2jvpe35hmxwgx">{source ? 'Choose…' : 'Choose a source first'}</option>
                  {source?.passages.map(item => <option key={item.id} value={item.id} data-id="element-2199" data-mp="2ldxlkwud23hm">
                      {item.id}
                    </option>)}
                </select>
                {errorFor('passageId')}
              </div>
            </div>
          </fieldset>

          <div className="mt-8" data-id="element-2200" data-mp="2hahnrx6k1x3c">
            <label htmlFor="interpretation" className={labelClass} data-id="element-2201" data-mp="2h9ptqqslw7f6">
              Interpretation
            </label>
            <p className="text-xs text-ink-muted" data-id="element-2202" data-mp="22ktkiqetcocz">What the passage supports, in your words.</p>
            <textarea id="interpretation" rows={3} value={draft.interpretation} onChange={event => update({
            interpretation: event.target.value
          })} className={fieldClass} {...describe('interpretation')} data-id="element-2203" data-mp="27f7lr9g5pppk" />
            {errorFor('interpretation')}
          </div>
          <div className="mt-5" data-id="element-2204" data-mp="1olu05uyc2vmm">
            <label htmlFor="limitation" className={labelClass} data-id="element-2205" data-mp="1ooy7v2b0c9i1">
              Population, context and limitations
            </label>
            <textarea id="limitation" rows={3} value={draft.limitation} onChange={event => update({
            limitation: event.target.value
          })} className={fieldClass} {...describe('limitation')} data-id="element-2206" data-mp="1028rdl6czsam" />
            {errorFor('limitation')}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-line pt-6" data-id="element-2207" data-mp="1nymr0cx103rp">
            <button type="submit" className={primaryButton} data-id="element-2208" data-mp="1j037sk9th631">
              Submit for review
            </button>
            <p className="text-xs text-ink-muted" data-id="element-2209" data-mp="1xp7lpp9kwo5c">Manual proposal · no AI extraction runs in this demonstration.</p>
          </div>
        </form>

        <aside aria-label="Selected passage" className="xl:sticky xl:top-28 xl:self-start" data-id="element-2210" data-mp="26q5t8hj87dso">
          <div className={`${glassPanel} p-6`} data-id="element-2211" data-mp="22p3u00pb71qp">
            <p className="text-sm font-semibold text-ink-dark" data-id="element-2212" data-mp="22qexdzeue7ha">Selected passage</p>
            {passage && source ? <>
                <p className="mt-1 font-mono text-xs font-semibold text-ink-muted" data-id="element-2213" data-mp="3e2wc7q99ju4v">
                  Source text · {source.id} · {passage.id}
                </p>
                <blockquote className="mt-3 border-l-4 border-lavender pl-4 font-serif text-[1.05rem] leading-relaxed text-ink-dark" data-id="element-2214" data-mp="3j2h9zxcp5wg0">
                  “{passage.text}”
                </blockquote>
                <dl className="mt-5 space-y-2 text-sm" data-id="element-2215" data-mp="2qev8qvqxz3wb">
                  {[['Design', source.design], ['Population', source.population], ['Source limits', source.limit]].map(([term, value]) => <div key={term} data-id="element-2216" data-mp="345qkcfstrz3l">
                      <dt className="inline font-medium text-ink-muted" data-id="element-2217" data-mp="346taglibpxig">{term}: </dt>
                      <dd className="inline text-ink-dark" data-id="element-2218" data-mp="27masqewcct9x">{value}</dd>
                    </div>)}
                </dl>
              </> : <p className="mt-2 text-sm leading-relaxed text-ink-muted" data-id="element-2219" data-mp="243tvpns07fld">
                Choose a source and passage. The exact text appears here so the interpretation can be written against it.
              </p>}
          </div>
        </aside>
      </div>
    </div>;
}